import { createReducer, current } from '@reduxjs/toolkit';
import get from 'lodash/get';
import set from 'lodash/set';

import {
  applyTextBundle,
  bulkEditLanguageValuesForCampaignLines,
  bulkEditPartialLPUrlForCampaignLines,
  changeCampaignLine,
  changeCampaignLines,
  clearTextAssetsForProductsUsingCollectionIds,
  clearTextAssetsForProductsUsingContentIds,
  filterCampaignLines,
  push,
  removeLanguageLine,
  submit,
} from '../actions';
import { languagesRange } from '../constants';
import {
  findLanguageLineNumber,
  getFormattedDate,
  getPartiallyUpdatedLPUrl,
  isChangeRestricted,
  isEditable,
  replacedBrandPlaceholders,
} from '../utils';
import { isLineFilteredOut } from '../utils/filter-utils';

const textAssets = ['title', 'subTitle', 'cta', 'desc', 'landingPage'];
const languageFields = ['language', ...textAssets];

const TEXT_ASSETS = ['title', 'subTitle', 'cta'];
const EMPTY_BUNDLE = TEXT_ASSETS.reduce(
  (acc, cur) => ({ ...acc, [cur]: '' }),
  {},
);

export default createReducer(
  {},
  /* eslint-disable no-param-reassign */
  {
    [changeCampaignLine]: (state, { payload: { name, value, markDirty } }) => {
      const [id, prop] = name.split('.');
      const line = get(state, id);

      if (!isChangeRestricted(line, prop)) {
        set(state, name, value);
        if (markDirty) set(state, [id, 'isDirty'], true);
      }
    },

    [changeCampaignLines]: (
      state,
      { payload: { ids, name, value, markDirty } },
    ) => {
      ids.forEach(id => {
        const line = get(state, id);

        if (!isChangeRestricted(line, name)) {
          set(state, [id, name], value);
          if (markDirty) set(state, [id, 'isDirty'], true);
        }
      });
    },

    [applyTextBundle]: (
      state,
      { payload: { brand, campaignLineTextBundle, selectedLineIds } },
    ) => {
      const lines = selectedLineIds
        ? selectedLineIds.map(id => state[id])
        : Object.values(state);

      lines.forEach(line => {
        if (!isEditable(line)) {
          return;
        }
        languagesRange.forEach(index => {
          const { countryCode } = line;
          const language = line[`language${index}`];

          if (!language) {
            return;
          }

          const campaignBundle =
            campaignLineTextBundle.campaignLineTextBundleItems.find(
              item =>
                item.country === countryCode && item.language === language,
            ) || EMPTY_BUNDLE;
          TEXT_ASSETS.forEach(assetName => {
            if (isChangeRestricted(line, assetName)) {
              return;
            }
            const value = replacedBrandPlaceholders(
              campaignBundle[assetName],
              brand,
            );
            set(state, [line.id, `${assetName}${index}`], value);
            set(state, [line.id, 'isDirty'], true);
          });
        });
      });
    },

    [bulkEditLanguageValuesForCampaignLines]: (
      state,
      { payload: { ids, languages, name, value } },
    ) => {
      ids.forEach(id => {
        languages.forEach(language => {
          const line = get(state, id);
          const languageIndex = findLanguageLineNumber(line, language);
          if (!languageIndex || isChangeRestricted(line, name)) {
            return;
          }
          if (name.includes('emphasisedText')) {
            const dateFormat = current(state)
              [id][`emphasisedText${languageIndex}`].split('[')
              .pop()
              .split(']')[0];
            const formattedDate = getFormattedDate(value, dateFormat);
            set(
              state,
              [id, `emphasisedText${languageIndex}`],
              current(state)[id][`emphasisedText${languageIndex}`].replace(
                /\[.*\]/g,
                formattedDate,
              ),
            );
            set(state, [id, 'isDirty'], true);
          }
          // replaces the value provided in between [] for availableDescription
          else if (name.includes('availableDescription')) {
            set(
              state,
              [id, `${name}${languageIndex}`],
              current(state)[id][`${name}${languageIndex}`].replace(
                /\[.*\]/g,
                value,
              ),
            );
            set(state, [id, 'isDirty'], true);
          } else {
            set(state, [id, `${name}${languageIndex}`], value);
            set(state, [id, 'isDirty'], true);
          }
        });
      });
    },
    [bulkEditPartialLPUrlForCampaignLines]: (
      state,
      { payload: { ids, languages, name, value } },
    ) => {
      ids.forEach(id => {
        languages.forEach(language => {
          const line = get(state, id);
          const languageIndex = findLanguageLineNumber(line, language);
          const currentLandingPage = state[id][`landingPage${languageIndex}`];
          if (
            !languageIndex ||
            isChangeRestricted(line, name) ||
            !currentLandingPage
          ) {
            return;
          }
          set(
            state,
            [id, `landingPage${languageIndex}`],
            getPartiallyUpdatedLPUrl(currentLandingPage, name, value),
          );
          set(state, [id, 'isDirty'], true);
        });
      });
    },

    [removeLanguageLine]: (state, { payload: { id, languageCode } }) => {
      const line = state[id];
      const languageNumber = findLanguageLineNumber(line, languageCode);

      if (languageNumber) {
        languageFields.forEach(field => {
          set(state, [id, `${field}${languageNumber}`], '');
          set(state, [id, 'isDirty'], true);
        });
      }
    },

    [`${submit}-success`]: (state, { payload }) => payload.campaignLinesById,
    [`${push}-success`]: (state, { payload }) => payload.campaignLinesById,

    [filterCampaignLines]: (state, { payload: { selectedFilters } }) => {
      Object.values(state).forEach(line => {
        line.filteredOut = isLineFilteredOut(line, selectedFilters);
      });
    },
    [clearTextAssetsForProductsUsingCollectionIds]: (
      state,
      { payload: { lineIdsToClean } },
    ) => {
      lineIdsToClean.forEach(id => {
        textAssets.forEach(field => {
          languagesRange.forEach(languageNumber => {
            set(state, [id, `${field}${languageNumber}`], '');
            set(state, [id, 'isDirty'], true);
          });
        });
      });
    },

    [clearTextAssetsForProductsUsingContentIds]: (
      state,
      { payload: { lineIdsToClean } },
    ) => {
      lineIdsToClean.forEach(id => {
        textAssets.forEach(field => {
          languagesRange.forEach(languageNumber => {
            set(state, [id, `${field}${languageNumber}`], '');
            set(state, [id, 'isDirty'], true);
          });
        });
      });
    },
  },
  /* eslint-enable no-param-reassign */
);
